/**
 * API Service for handling communication with the backend
 *
 * This service provides methods for making HTTP requests to the API endpoints
 * and handles common concerns like authentication, error handling, and response parsing.
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { PaginatedResponse, SearchFilters } from '../types';

// Base API URL
const API_URL = '/api';

class ApiService {
  private client: AxiosInstance;
  private language: string = 'en'; // Default language

  constructor() {
    // Initialize axios instance with default config
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => this.handleError(error)
    );
  }

  /**
   * Set the current language for API requests
   * @param lang - Language code (e.g., 'en', 'fr', 'es')
   */
  setLanguage(lang: string): void {
    this.language = lang;
    this.client.defaults.headers.common['Accept-Language'] = lang;
  }

  /**
   * Get the current language
   * @returns Current language code
   */
  getLanguage(): string {
    return this.language;
  }

  /**
   * Make a GET request to the API
   * @param url - API endpoint URL
   * @param config - Additional axios config
   * @returns Promise with the response data
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  /**
   * Make a POST request to the API
   * @param url - API endpoint URL
   * @param data - Data to send in the request body
   * @param config - Additional axios config
   * @returns Promise with the response data
   */
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  /**
   * Make a PUT request to the API
   * @param url - API endpoint URL
   * @param data - Data to send in the request body
   * @param config - Additional axios config
   * @returns Promise with the response data
   */
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  /**
   * Make a DELETE request to the API
   * @param url - API endpoint URL
   * @param config - Additional axios config
   * @returns Promise with the response data
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /**
   * Handle API errors
   * @param error - Error object from axios
   * @returns Rejected promise with error details
   */
  private handleError(error: any): Promise<never> {
    const errorResponse = {
      status: error.response?.status || 500,
      statusText: error.response?.statusText || 'Internal Server Error',
      data: error.response?.data || { message: 'An unexpected error occurred' },
      isOffline: !navigator.onLine,
      isTimeout: error.code === 'ECONNABORTED',
    };

    // Log error to console in development
    if (process.env.NODE_ENV !== 'production') {
      console.error('API Error:', errorResponse);
    }

    return Promise.reject(errorResponse);
  }

  /**
   * Build query parameters for search filters
   * @param filters - Search filters object
   * @returns URL query string
   */
  buildQueryParams(filters: SearchFilters): string {
    const params = new URLSearchParams();

    // Add each filter to params if it exists
    if (filters.query) params.append('query', filters.query);
    if (filters.status) params.append('status', filters.status);
    if (filters.language) params.append('language', filters.language);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);

    return params.toString();
  }
}

// Create a singleton instance
const apiService = new ApiService();

export default apiService;
